import { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../styles/FloatingCalendly.module.css';

// Public Calendly booking link. hide_event_type_details + hide_gdpr_banner keep
// the narrow panel clean; primary_color matches the site accent.
const CALENDLY_URL =
  'https://calendly.com/inspektlabs_book-a-demo/30min' +
  '?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=2f6fed';
const WIDGET_JS = 'https://assets.calendly.com/assets/external/widget.js';
const WIDGET_CSS = 'https://assets.calendly.com/assets/external/widget.css';

/**
 * Floating "Book a demo" launcher (fixed bottom-right, site-wide) that opens a
 * chat-widget-style panel with the Calendly scheduler embedded inline — not
 * Calendly's full-screen modal. The panel chrome is our design; the scheduler
 * renders in its narrow single-column layout inside it.
 *
 * Calendly's assets + the inline widget are created lazily on first open, so
 * there's no page-load cost. A completed booking pushes a `demo_booked` event
 * to the GTM dataLayer.
 */
export default function FloatingCalendly() {
  const containerRef = useRef(null);
  const mountedRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Track completed bookings via Calendly's postMessage events.
  useEffect(() => {
    const isCalendlyEvent = (e) =>
      typeof e?.origin === 'string' &&
      e.origin.indexOf('calendly.com') !== -1 &&
      e?.data &&
      typeof e.data.event === 'string' &&
      e.data.event.indexOf('calendly.') === 0;

    const onMessage = (e) => {
      if (!isCalendlyEvent(e)) return;
      if (e.data.event === 'calendly.event_scheduled') {
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'demo_booked',
            booking_source: 'floating_calendly',
          });
        } catch (err) {
          /* tracking is best-effort — never fatal */
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Inject Calendly's CSS + JS once, resolving when window.Calendly is ready.
  const ensureAssets = useCallback(
    () =>
      new Promise((resolve) => {
        if (typeof window === 'undefined') return;

        const cssReady = new Promise((res) => {
          if (document.querySelector('link[data-calendly]')) {
            res();
            return;
          }
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = WIDGET_CSS;
          link.setAttribute('data-calendly', 'true');
          link.onload = () => res();
          link.onerror = () => res();
          document.head.appendChild(link);
        });

        const scriptReady = new Promise((res) => {
          const waitForGlobal = () => {
            let tries = 0;
            const poll = () => {
              if (window.Calendly || tries > 100) {
                res();
                return;
              }
              tries += 1;
              setTimeout(poll, 50);
            };
            poll();
          };

          if (window.Calendly) {
            res();
            return;
          }
          const existing = document.querySelector('script[data-calendly]');
          if (existing) {
            existing.addEventListener('load', waitForGlobal);
            waitForGlobal();
            return;
          }
          const script = document.createElement('script');
          script.src = WIDGET_JS;
          script.async = true;
          script.setAttribute('data-calendly', 'true');
          script.onload = waitForGlobal;
          document.body.appendChild(script);
        });

        Promise.all([cssReady, scriptReady]).then(() => resolve());
      }),
    []
  );

  // Mount the inline widget the first time the panel opens.
  useEffect(() => {
    if (!open || mountedRef.current) return undefined;

    let cancelled = false;
    setLoading(true);
    ensureAssets().then(() => {
      if (cancelled) return;
      if (window.Calendly && containerRef.current) {
        window.Calendly.initInlineWidget({
          url: CALENDLY_URL,
          parentElement: containerRef.current,
        });
        mountedRef.current = true;
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, ensureAssets]);

  const toggle = useCallback(() => setOpen((v) => !v), []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <div
        className={`${styles.panel} ${open ? styles.panelOpen : ''}`}
        role="dialog"
        aria-label="Book a demo"
        aria-hidden={!open}
        // When closed, `inert` removes the panel + its focusable descendants
        // (Close button, Calendly iframe) from the tab order and accessibility
        // tree — fixes the a11y audit about focusable content inside
        // aria-hidden. `visibility:hidden` in the CSS is the cross-browser
        // backstop for the same thing.
        {...(open ? {} : { inert: '' })}
      >
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>Book a demo</span>
          <button
            type="button"
            className={styles.panelClose}
            aria-label="Close"
            onClick={close}
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className={styles.panelBody}>
          {loading && (
            <div className={styles.loading} role="status" aria-label="Loading scheduler">
              <span className={styles.spinner} />
            </div>
          )}
          <div ref={containerRef} className={styles.widget} />
        </div>
      </div>

      <button
        type="button"
        className={styles.fab}
        aria-label={open ? 'Close scheduler' : 'Book a demo'}
        aria-expanded={open}
        onClick={toggle}
      >
        {open ? (
          <svg
            className={styles.fabIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <>
            <svg
              className={styles.fabIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span className={styles.fabLabel}>Book a demo</span>
          </>
        )}
      </button>
    </>
  );
}
